import firehydrantAPI from 'helpers/firehydrantAPI';
import { createMutationHook, useApiQuery } from 'queries/common';

const NOTIFICATION_POLICY_KEY = 'notification_policy_items';

// API Response wrapper type
type ApiResponse<T> = {
  data: T;
};

// Types based on actual API contract
interface NotificationPolicyItem {
  id: string;
  notification_group_method: string;
  max_delay: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  created_at?: string;
  updated_at?: string;
}

// Legacy types for backward compatibility with existing components
interface NotificationPolicyStep {
  id?: string;
  delay: string;
  notification_type: {
    label: string;
    value: string;
  } | null;
  isNew?: boolean;
}

interface NotificationPolicyPriority {
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  steps: NotificationPolicyStep[];
}

interface NotificationPolicy {
  id: string;
  name: string;
  priorities: NotificationPolicyPriority[];
  created_at?: string;
  updated_at?: string;
}

interface UserPolicyCompliance {
  user_id: string;
  in_policy: boolean;
  non_compliant_priorities?: Array<'HIGH' | 'MEDIUM' | 'LOW'>;
  compliance_details?: {
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    compliant_steps: number[];
    non_compliant_steps: number[];
  }[];
}

// Hook to fetch notification policy items
export const useNotificationPolicyItems = () => {
  const query = useApiQuery([NOTIFICATION_POLICY_KEY, 'items'], () =>
    firehydrantAPI.get('signals/notification_policy_items'),
  );

  return {
    ...query,
    data: (
      query.data as
        | ApiResponse<ApiResponse<NotificationPolicyItem[]>>
        | undefined
    )?.data?.data,
  };
};

// Hook to create a new notification policy item
const createNotificationPolicyItem = (
  params: Omit<NotificationPolicyItem, 'id' | 'created_at' | 'updated_at'>,
) => firehydrantAPI.post('signals/notification_policy_items', params);

export const useCreateNotificationPolicyItem = createMutationHook(
  createNotificationPolicyItem,
);

// Hook to update an existing notification policy item
const updateNotificationPolicyItem = (
  params: Omit<NotificationPolicyItem, 'created_at' | 'updated_at'>,
) =>
  firehydrantAPI.patch(`signals/notification_policy_items/${params.id}`, {
    notification_group_method: params.notification_group_method,
    max_delay: params.max_delay,
    priority: params.priority,
  });

export const useUpdateNotificationPolicyItem = createMutationHook(
  updateNotificationPolicyItem,
);

// Hook to delete a notification policy item
const deleteNotificationPolicyItem = (id: string) =>
  firehydrantAPI.delete(`signals/notification_policy_items/${id}`);

export const useDeleteNotificationPolicyItem = createMutationHook(
  deleteNotificationPolicyItem,
);

// Legacy hook for backward compatibility - converts policy items to old structure
export const useNotificationPolicy = () => {
  const { data: policyItems, ...query } = useNotificationPolicyItems();

  // Mapping from API notification_group_method to human-readable labels
  const getNotificationTypeLabel = (method: string): string => {
    const typeMap: Record<string, string> = {
      push: 'Push Notification',
      mobile_text: 'SMS or WhatsApp',
      voice: 'Phone Call',
      email: 'Email',
      chat: 'Slack or MS Teams',
      any: 'Any',
    };
    return typeMap[method] || method;
  };

  // Convert policy items to legacy policy structure for existing components
  const convertToLegacyFormat = (
    items: NotificationPolicyItem[],
  ): NotificationPolicy => {
    const priorities: NotificationPolicyPriority[] = [
      'HIGH',
      'MEDIUM',
      'LOW',
    ].map((priority) => ({
      priority: priority as 'HIGH' | 'MEDIUM' | 'LOW',
      steps: items
        .filter((item) => item.priority === priority)
        .map((item) => ({
          id: item.id,
          delay: item.max_delay,
          notification_type: {
            label: getNotificationTypeLabel(item.notification_group_method),
            value: item.notification_group_method,
          },
        })),
    }));

    return {
      id: 'converted-policy',
      name: 'Organization Notification Policy',
      priorities,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  };

  return {
    ...query,
    data: policyItems ? convertToLegacyFormat(policyItems) : undefined,
  };
};

// Hook to check if a specific user is in compliance with the policy
export const useUserPolicyCompliance = (options: { enabled: boolean }) => {
  const query = useApiQuery(
    [NOTIFICATION_POLICY_KEY, 'detail', 'compliance'],
    () => firehydrantAPI.get(`signals_notification_settings/compliance`),
    {
      enabled: options.enabled,
    },
  );

  return {
    ...query,
    data: (query.data as ApiResponse<UserPolicyCompliance> | undefined)?.data
      ?.data,
  };
};

// Hook to fetch multiple users' compliance status (for team views)
export const useTeamPolicyCompliance = (userIds: string[]) => {
  const query = useApiQuery(
    [NOTIFICATION_POLICY_KEY, 'compliance', 'batch', userIds],
    () =>
      firehydrantAPI.post('users/policy_compliance/batch', {
        user_ids: userIds,
      }),
    {
      enabled: userIds.length > 0,
    },
  );

  return {
    ...query,
    data: (query.data as ApiResponse<UserPolicyCompliance[]> | undefined)?.data,
  };
};

// Hook to validate a set of notification preferences against the policy
export const useValidateNotificationPreferences = () => {
  const validatePreferences = (preferences: any) =>
    firehydrantAPI.post('signals/notification_preference_policy/validate', {
      preferences,
    });

  return createMutationHook(validatePreferences);
};

// Export types for use in components
export type {
  NotificationPolicy,
  NotificationPolicyStep,
  NotificationPolicyPriority,
  NotificationPolicyItem,
  UserPolicyCompliance,
};
